import { useCallback, useRef, useState } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { useAuth } from './useAuth'
import { reportMarketDataStatus } from '../lib/marketDataStatusEvents'

async function readJson(response) {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(body.error || 'Market-data operation failed')
  return body
}

export function useMarketDataOperations() {
  const { sessionRef } = useAuth()
  const [status, setStatus] = useState(null)
  const [busyAction, setBusyAction] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const refreshPromiseRef = useRef(null)

  const token = useCallback(() => {
    const value = sessionRef?.current?.access_token
    if (!value) throw new Error('Session expired. Sign in again.')
    return value
  }, [sessionRef])

  const refresh = useCallback(() => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current

    setRefreshing(true)
    const request = (async () => {
      const result = await readJson(await fetch('/api/market-data/operations-status', {
        headers: { Authorization: `Bearer ${token()}` },
      }))
      setStatus(result)
      reportMarketDataStatus(result)
      return result
    })().finally(() => {
      refreshPromiseRef.current = null
      setRefreshing(false)
    })
    refreshPromiseRef.current = request
    return request
  }, [token])

  const restart = useCallback(async (action) => {
    setBusyAction(action)
    try {
      const challenge = await readJson(await fetch('/api/market-data/operations-challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ action }),
      }))
      if (!challenge.hasPasskeys) {
        throw new Error('Passkey required. Add one in Account & Security first.')
      }

      const { challengeId, intent, hasPasskeys, ...authenticationOptions } = challenge
      let assertion
      try {
        assertion = await startAuthentication({ optionsJSON: authenticationOptions })
      } catch (error) {
        if (error?.name === 'NotAllowedError' || /timed out|not allowed/i.test(error?.message || '')) {
          throw new Error('Passkey verification was canceled or timed out. Try again.')
        }
        throw error
      }
      const verification = await readJson(await fetch('/api/market-data/operations-verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ action, challengeId, intent, response: assertion }),
      }))
      const result = await readJson(await fetch('/api/market-data/operations-action', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ action, grant: verification.grant }),
      }))
      return result
    } finally {
      setBusyAction(null)
    }
  }, [token])

  return { status, refresh, restart, busyAction, refreshing }
}
